import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle, RefreshCw, X, Loader2, Package, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface Decisao {
  id: number
  pedido_id: number
  pedido_numero: number
  pedido_item_id: number
  item_descricao: string
  item_quantidade: number
  cliente_nome: string | null
  op_origem_id: number | null
  op_origem_numero: number | null
  lote_id: number | null
  lote_codigo: string | null
  op_proposta_id: number | null
  op_proposta_numero: number | null
  qualidade_exigida: string
  qualidade_real: string
  status: string
  criada_em: string
}

const corQualidade: Record<string, string> = {
  PRIMEIRA: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  SEGUNDA: 'bg-amber-100 text-amber-800 border-amber-300',
  TERCEIRA: 'bg-red-100 text-red-800 border-red-300',
}

export default function DecisoesQualidadePage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [statusFiltro, setStatusFiltro] = useState<'pendente' | 'todos'>('pendente')
  const [acaoModal, setAcaoModal] = useState<{
    decisao: Decisao
    tipo: 'aceitar' | 'reproduzir' | 'cancelar'
  } | null>(null)
  const [motivo, setMotivo] = useState('')

  // ESC fecha modal de ação (a11y)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && acaoModal) {
        setAcaoModal(null)
        setMotivo('')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [acaoModal])

  const { data: decisoes = [], isLoading } = useQuery<Decisao[]>({
    queryKey: ['qualidade-decisoes', statusFiltro],
    queryFn: async () => (await api.get(`/vendas/qualidade?status_filtro=${statusFiltro}`)).data,
    refetchInterval: 30_000,
  })

  const acaoMut = useMutation({
    mutationFn: async () => {
      if (!acaoModal) return
      const path = acaoModal.tipo === 'aceitar' ? 'aceitar'
        : acaoModal.tipo === 'reproduzir' ? 'reproduzir'
        : 'cancelar-item'
      return (await api.post(`/vendas/qualidade/${acaoModal.decisao.id}/${path}`, { motivo })).data
    },
    onSuccess: () => {
      toast.success('Decisão registrada')
      setAcaoModal(null); setMotivo('')
      qc.invalidateQueries({ queryKey: ['qualidade-decisoes'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao registrar'),
  })

  const pendentes = decisoes.filter(d => d.status === 'pendente')

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="p-2 rounded-lg hover:bg-ink-100 text-ink-600">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-ink-900 flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={22} />
            Decisões de qualidade
          </h1>
          <p className="text-xs text-ink-500">
            Pedidos com OP concluída em qualidade abaixo do exigido — vendedor decide o que fazer
          </p>
        </div>
        {pendentes.length > 0 && (
          <span className="bg-amber-100 text-amber-900 px-3 py-1.5 rounded-full text-sm font-bold">
            {pendentes.length} pendente{pendentes.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setStatusFiltro('pendente')}
          className={`px-3 py-1 rounded-full text-sm border ${
            statusFiltro === 'pendente' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-ink-700 border-ink-300'
          }`}>
          Pendentes
        </button>
        <button onClick={() => setStatusFiltro('todos')}
          className={`px-3 py-1 rounded-full text-sm border ${
            statusFiltro === 'todos' ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-ink-700 border-ink-300'
          }`}>
          Todas
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12 text-ink-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Carregando…
        </div>
      )}

      {!isLoading && decisoes.length === 0 && (
        <div className="text-center py-16 text-ink-400">
          <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nenhuma decisão {statusFiltro === 'pendente' ? 'pendente' : ''}</p>
        </div>
      )}

      <div className="space-y-3">
        {decisoes.map(d => (
          <div key={d.id} className="bg-white border border-ink-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-ink-900">Pedido #{d.pedido_numero}</span>
                  {d.cliente_nome && <span className="text-sm text-ink-600">— {d.cliente_nome}</span>}
                  {d.status !== 'pendente' && (
                    <span className="text-[10px] uppercase bg-ink-100 text-ink-600 px-2 py-0.5 rounded font-semibold">
                      {d.status.replace('_', ' ')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-ink-700 mb-2">{d.item_descricao}</div>
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <span className="text-ink-500">Qtd: <strong>{d.item_quantidade}</strong></span>
                  {d.op_origem_numero && (
                    <span className="text-ink-500">OP origem: <strong>#{d.op_origem_numero}</strong></span>
                  )}
                  {d.lote_codigo && (
                    <span className="text-ink-500">Lote: <strong>{d.lote_codigo}</strong></span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-ink-500">Cliente exigia:</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${corQualidade[d.qualidade_exigida]}`}>
                    {d.qualidade_exigida}
                  </span>
                  <span className="text-xs text-ink-500">→ Saiu:</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${corQualidade[d.qualidade_real]}`}>
                    {d.qualidade_real}
                  </span>
                </div>
                {d.op_proposta_numero && d.status === 'pendente' && (
                  <div className="mt-2 text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1 inline-flex items-center gap-1">
                    <Package size={12} /> OP-proposta #{d.op_proposta_numero} já criada (aguardando aprovação)
                  </div>
                )}
              </div>

              {d.status === 'pendente' && (
                <div className="flex flex-col gap-2 min-w-[220px]">
                  <button
                    onClick={() => { setAcaoModal({ decisao: d, tipo: 'aceitar' }); setMotivo('') }}
                    className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                    <CheckCircle size={14} /> Cliente aceitou {d.qualidade_real.toLowerCase()}
                  </button>
                  <button
                    onClick={() => { setAcaoModal({ decisao: d, tipo: 'reproduzir' }); setMotivo('') }}
                    className="px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                    <RefreshCw size={14} /> Produzir de novo
                  </button>
                  <button
                    onClick={() => { setAcaoModal({ decisao: d, tipo: 'cancelar' }); setMotivo('') }}
                    className="px-3 py-2 rounded-lg border border-red-300 hover:bg-red-50 text-red-700 text-sm font-semibold flex items-center justify-center gap-1.5">
                    <X size={14} /> Cancelar item
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal de confirmação de ação */}
      {acaoModal && (
        <div className="fixed inset-0 bg-ink-950/85 z-[60] flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-labelledby="decisao-modal-titulo">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 id="decisao-modal-titulo" className="text-lg font-bold text-ink-900 mb-1">
              {acaoModal.tipo === 'aceitar' && `Cliente aceitou ${acaoModal.decisao.qualidade_real.toLowerCase()}?`}
              {acaoModal.tipo === 'reproduzir' && 'Produzir de novo?'}
              {acaoModal.tipo === 'cancelar' && 'Cancelar item do pedido?'}
            </h3>
            <p className="text-sm text-ink-600 mb-3">
              {acaoModal.tipo === 'aceitar' && (
                <>O lote {acaoModal.decisao.lote_codigo || ''} fica disponível pra atender o pedido. OP-proposta #{acaoModal.decisao.op_proposta_numero} será cancelada.</>
              )}
              {acaoModal.tipo === 'reproduzir' && (
                <>OP-proposta #{acaoModal.decisao.op_proposta_numero} vai pra status "planejada" e o PCP retoma a produção. O lote {acaoModal.decisao.lote_codigo || ''} ({acaoModal.decisao.qualidade_real.toLowerCase()}) fica livre no estoque.</>
              )}
              {acaoModal.tipo === 'cancelar' && (
                <>A linha do pedido será zerada e a OP-proposta cancelada. Cliente precisa ser avisado.</>
              )}
            </p>
            <textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder={acaoModal.tipo === 'cancelar'
                ? 'Motivo (obrigatório)'
                : 'Motivo (opcional, mas recomendado pra auditoria)'}
              rows={3}
              className="w-full text-sm border border-ink-300 rounded p-2 focus:border-brand-500 outline-none mb-4"
            />
            <div className="flex gap-2">
              <button onClick={() => {
                  if (motivo.trim() && !confirm('Você digitou um motivo. Sair sem salvar?')) return
                  setAcaoModal(null); setMotivo('')
                }}
                className="flex-1 py-2 rounded-lg border border-ink-300 text-ink-700 font-semibold hover:bg-ink-50">
                Voltar
              </button>
              <button
                disabled={acaoMut.isPending || (acaoModal.tipo === 'cancelar' && !motivo.trim())}
                onClick={() => acaoMut.mutate()}
                className={'flex-1 py-2 rounded-lg font-semibold text-white ' +
                  (acaoMut.isPending || (acaoModal.tipo === 'cancelar' && !motivo.trim())
                    ? 'bg-ink-300 cursor-not-allowed'
                    : acaoModal.tipo === 'cancelar' ? 'bg-red-600 hover:bg-red-700'
                    : acaoModal.tipo === 'reproduzir' ? 'bg-sky-600 hover:bg-sky-700'
                    : 'bg-emerald-600 hover:bg-emerald-700')}>
                {acaoMut.isPending ? <Loader2 className="animate-spin inline mr-1" size={14} /> : null}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
